import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const CreateEstablishment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [created, setCreated] = useState(null);

  const [formData, setFormData] = useState({
    // Lycée
    name: '',
    email: '',
    phone: '',
    address: '',
    // Admin
    admin_name: '',
    admin_email: '',
    admin_password: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    alert('📋 Copié !');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setCreated(null);

    const submittedPassword = formData.admin_password;

    try {
      const token = localStorage.getItem('token');

      const res = await fetch('/api/super-admin/establishments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.errors) {
          const errs = Object.values(data.errors).flat().join('\n');
          setMessage({ type: 'error', text: errs });
        } else {
          setMessage({ type: 'error', text: data.error || data.message || 'Erreur' });
        }
      } else {
        setMessage({
          type: 'success',
          text: `✅ Lycée "${data.establishment.name}" créé avec succès !`,
        });

        setCreated({
          establishment: data.establishment,
          admin: data.admin,
          password: submittedPassword,
        });

        setFormData({
          name: '', email: '', phone: '', address: '',
          admin_name: '', admin_email: '', admin_password: '',
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 md:py-10 md:px-6">
      <div className="max-w-2xl mx-auto">

        {/* En-tête */}
        <div className="mb-6 md:mb-8">
          <button
            onClick={() => navigate('/super-admin/establishments')}
            className="text-sm text-gray-500 hover:text-[#F39C12] mb-3 flex items-center gap-1"
          >
            ← Retour aux lycées
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-[#2C3E50]">
            ➕ Créer un nouveau lycée
          </h1>
          <p className="text-gray-600 mt-2 text-sm md:text-base">
            Remplissez les informations du lycée et de son administrateur.
          </p>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg whitespace-pre-line text-sm md:text-base ${
              message.type === 'error'
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Récap après création */}
        {created && (
          <div className="mb-6 bg-white rounded-2xl shadow-lg border-l-4 border-[#F39C12] p-5 md:p-6">
            <h2 className="text-lg font-bold text-[#2C3E50] mb-4">
              🔑 Identifiants du nouvel administrateur
            </h2>

            <div className="bg-orange-50 rounded-lg p-4 mb-4 text-sm text-orange-800 border border-orange-200">
              ⚠️ <strong>Important :</strong> Notez ces identifiants maintenant.
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 mb-1">🏫 Lycée</div>
                  <div className="font-semibold text-[#2C3E50] truncate">
                    {created.establishment?.name}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-gray-50 p-3 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 mb-1">📧 Email</div>
                  <div className="font-mono font-semibold text-[#2C3E50] break-all text-sm">
                    {created.admin?.email}
                  </div>
                </div>
                <button
                  onClick={() => handleCopy(created.admin?.email || '')}
                  className="sm:ml-3 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 transition self-start sm:self-auto"
                >
                  📋 Copier
                </button>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-gray-50 p-3 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 mb-1">🔑 Mot de passe</div>
                  <div className="font-mono font-semibold text-[#2C3E50] break-all text-sm">
                    {created.password}
                  </div>
                </div>
                <button
                  onClick={() => handleCopy(created.password || '')}
                  className="sm:ml-3 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 transition self-start sm:self-auto"
                >
                  📋 Copier
                </button>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  const all = `Lycée : ${created.establishment?.name}\nEmail : ${created.admin?.email}\nMot de passe : ${created.password}`;
                  handleCopy(all);
                }}
                className="flex-1 py-2.5 bg-[#F39C12] text-white rounded-lg text-sm font-semibold hover:bg-[#E67E22] transition"
              >
                📋 Copier tout
              </button>
              <button
                onClick={() => setCreated(null)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                ✕ Masquer
              </button>
            </div>
          </div>
        )}

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-5 md:p-8 space-y-6 md:space-y-8">

          {/* ---- Section Lycée ---- */}
          <div>
            <h2 className="text-lg md:text-xl font-bold text-[#2C3E50] mb-4 pb-2 border-b-2 border-[#F39C12] inline-block">
              🏫 Informations du lycée
            </h2>

            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du lycée *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Lycée Moderne de Dakar"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F39C12]"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contact@lycee.sn"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F39C12]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+221 33 123 45 67"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F39C12]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Dakar, Sénégal"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F39C12]"
                />
              </div>
            </div>
          </div>

          {/* ---- Section Admin ---- */}
          <div>
            <h2 className="text-lg md:text-xl font-bold text-[#2C3E50] mb-4 pb-2 border-b-2 border-[#F39C12] inline-block">
              👤 Administrateur du lycée
            </h2>

            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <input
                  type="text"
                  name="admin_name"
                  value={formData.admin_name}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Mamadou Diallo"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F39C12]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="admin_email"
                  value={formData.admin_email}
                  onChange={handleChange}
                  required
                  placeholder="admin@lycee.sn"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F39C12]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <input
                  type="password"
                  name="admin_password"
                  value={formData.admin_password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  placeholder="Au moins 6 caractères"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F39C12]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ℹ️ L'admin devra changer son mot de passe à la première connexion.
                </p>
              </div>
            </div>
          </div>

          {/* Boutons */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/super-admin/establishments')}
              className="w-full sm:w-auto px-6 py-3.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:flex-1 bg-[#F39C12] text-white py-3.5 rounded-lg font-semibold hover:bg-[#E67E22] transition disabled:opacity-50"
            >
              {loading ? '⏳ Création...' : '🚀 Créer le lycée et son admin'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CreateEstablishment;